import {
  MANEDSNAVN,
  belopFraDesimaltekst,
  erForsinket,
  erManed,
  leveringsfrist,
  belopTilDesimaltekst,
  gjeldendeLevering,
  periodetilstand,
  type Periodetilstand,
  type PrognoseleveringDto,
  type VirksomhetsleveringerDto,
} from '@es/shared';

/**
 * Leveringene, gjort om til det skjermene viser.
 *
 * Reglene for hva som gjelder, ligger i @es/shared og brukes likt av serveren.
 * Her er bare det som er visning: tekster, og hvor mye prognosen har flyttet
 * seg siden forrige levering.
 */

export const STATUSTEKST: Record<Periodetilstand | 'erstattet', string> = {
  ikke_levert: 'Ikke levert',
  levert: 'Venter på godkjenning',
  godkjent: 'Godkjent',
  sendt_tilbake: 'Sendt tilbake',
  erstattet: 'Erstattet',
};

/** Tailwind-klasser for statusmerket. Rødt bare der noe må gjøres. */
export const STATUSFARGE: Record<Periodetilstand | 'erstattet', string> = {
  ikke_levert: 'bg-flate-senket text-tekst-dempet',
  levert: 'bg-varsel-svak text-tekst',
  godkjent: 'bg-positiv-svak text-tekst',
  sendt_tilbake: 'bg-negativ-svak text-tekst',
  erstattet: 'bg-flate-senket text-tekst-dempet',
};

/**
 * Den gjeldende leveringen for perioden før, eller undefined.
 *
 * «Perioden før» er den siste perioden som HAR en gjeldende levering — hoppet
 * noen over juni, er det mai som er forrige.
 */
export function forrigeLevering(
  leveringer: readonly PrognoseleveringDto[],
  periode: number,
): PrognoseleveringDto | undefined {
  const perioder = [...new Set(leveringer.map((l) => l.periode))]
    .filter((p) => p < periode)
    .sort((a, b) => b - a);
  for (const p of perioder) {
    const gjeldende = gjeldendeLevering(leveringer, p);
    if (gjeldende !== undefined) return gjeldende;
  }
  return undefined;
}

/**
 * Hvor mye årsprognosen har flyttet seg siden forrige levering, som
 * desimaltekst. Null når det ikke finnes en forrige.
 *
 * Det er spørsmålet tertialrapporten stiller: ikke «hva er avviket», men
 * «hvorfor har det vokst siden sist».
 */
export function endringSidenForrige(
  levering: PrognoseleveringDto,
  leveringer: readonly PrognoseleveringDto[],
): string | null {
  const forrige = forrigeLevering(leveringer, levering.periode);
  if (forrige === undefined) return null;
  return belopTilDesimaltekst(
    belopFraDesimaltekst(levering.prognose) - belopFraDesimaltekst(forrige.prognose),
  );
}

/** «10. juni» fra `2026-06-10`. Året tas med bare når det ikke er `ar`. */
export function formaterFrist(dato: string, ar: number): string {
  const [aret = '', maned = '', dag = ''] = dato.split('-');
  const m = Number(maned);
  const navn = erManed(m) ? MANEDSNAVN[m] : maned;
  return `${String(Number(dag))}. ${navn}${Number(aret) === ar ? '' : ` ${aret}`}`;
}

/**
 * Om virksomheten er forsinket med perioden som kan leveres nå.
 *
 * @param idag Dagens dato i norsk tid, se `datoIOslo`.
 */
export function erVirksomhetForsinket(
  v: VirksomhetsleveringerDto,
  ar: number,
  fristdag: number | null,
  idag: string,
): boolean {
  const tilstand = naavaerendeTilstand(v);
  if (v.periode === null || tilstand === null) return false;
  return erForsinket(tilstand, leveringsfrist(ar, v.periode, fristdag), idag);
}

/** Tilstanden for perioden som kan leveres nå, eller null når ingen kan det ennå. */
export function naavaerendeTilstand(v: VirksomhetsleveringerDto): Periodetilstand | null {
  return v.periode === null ? null : periodetilstand(v.leveringer, v.periode);
}

/** Opptellingen øverst i kommunalsjefens oversikt. */
export function opptelling(
  virksomheter: readonly VirksomhetsleveringerDto[],
  frist: { ar: number; fristdag: number | null; idag: string },
): {
  levert: number;
  venter: number;
  sendtTilbake: number;
  forsinket: number;
  medPeriode: number;
} {
  const tilstander = virksomheter.map(naavaerendeTilstand).filter((t) => t !== null);
  return {
    // Levert betyr her at det finnes noe å ta stilling til eller som er tatt
    // stilling til — ikke at det står «levert» i statusfeltet.
    levert: tilstander.filter((t) => t === 'levert' || t === 'godkjent').length,
    venter: tilstander.filter((t) => t === 'levert').length,
    sendtTilbake: tilstander.filter((t) => t === 'sendt_tilbake').length,
    forsinket: virksomheter.filter((v) =>
      erVirksomhetForsinket(v, frist.ar, frist.fristdag, frist.idag),
    ).length,
    medPeriode: tilstander.length,
  };
}
